import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect } from 'react-redux'
import redial from 'redial'
import { Helmet } from 'react-helmet'
import { createUrl } from '../../lib/utils'
import shareImg from '../../assets/ampparit-share-news.jpg'
import { setStatus } from '../../status/statusActions'
import {
  changeActiveList,
  fetchCategoryItems,
  fetchCategoryItemsNext,
  unqueueCategoryItems,
  LIST_TYPE_CATEGORY,
  LIST_TYPE_NONE,
  LIST_NAME_NONE,
} from '../../components/item/itemsActions'
import CategoryItemsEmpty from './CategoryItemsEmpty'
import TopNotification from '../../user/TopNotification'
import {
  selectIsUserLoggedIn,
  selectUserBlacklistedCategoryIds,
  selectUserWhitelistedCategoryIds,
} from '../../selectors/userSelector'
import NewsListWidgets from '../../components/lists/NewsListWidgets'
import { selectCategoryBySlug } from '../../selectors/categoriesSelector'
import ListOptions from '../../components/ui/ListOptions'
import { fetchPopular } from '../../components/item/popularActions'
import NewsListView from './NewsListView'
import { CATEGORY_ID_UUTISET } from '../../lib/constants'

@redial.provideHooks({
  fetch: ({ dispatch, getState, params: { category } }) => {
    const state = getState()
    const catRecord = selectCategoryBySlug(state, category)
    if (!catRecord) {
      return dispatch(setStatus(404))
    }

    dispatch(changeActiveList(LIST_TYPE_CATEGORY, category))
    return Promise.all([
      dispatch(fetchPopular(300, { categoryId: catRecord.id })),
      dispatch(fetchCategoryItems(category)),
    ])
  },
})
class CategoryView extends Component {
  constructor(props) {
    super(props)
    this.setActiveList = this.setActiveList.bind(this)
  }

  static propTypes = { // eslint-disable-line react/prefer-exact-props
    category: ImmutablePropTypes.recordOf({
      id: PropTypes.number.isRequired,
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      parentId: PropTypes.number.isRequired,
      treeLevel: PropTypes.number.isRequired,
      desc: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      h1: PropTypes.string.isRequired,
      relatedCategoryIds: ImmutablePropTypes.orderedSet,
      ogTitle: PropTypes.string,
      ogDesc: PropTypes.string,
      ogImage: PropTypes.string,
    }).isRequired,
    itemsForCategory: ImmutablePropTypes.map,
    dispatch: PropTypes.func.isRequired,
    path: PropTypes.string.isRequired,
    listBlacklisted: PropTypes.bool.isRequired,
    listWhitelisted: PropTypes.bool.isRequired,
    trendingFailedToLoad: PropTypes.bool,
    trending: ImmutablePropTypes.map,
    params: PropTypes.shape({
      category: PropTypes.string.isRequired,
    }).isRequired,
  }

  handleListDidUpdate(prevProps, props) {
    const { dispatch, category } = props

    if (prevProps.category !== category) {
      dispatch(changeActiveList(LIST_TYPE_CATEGORY, category.get('slug')))
    }
  }

  setActiveList() {
    const { dispatch, category } = this.props

    dispatch(changeActiveList(LIST_TYPE_CATEGORY, category.get('slug')))
  }

  refreshList = () => {
    const { dispatch, params: { category } } = this.props
    dispatch(fetchCategoryItems(category))
  }

  fetchNext = (timestamp) => {
    const { dispatch, params: { category } } = this.props
    dispatch(fetchCategoryItemsNext(category, timestamp))
  }

  unqueueItems = () => {
    const { dispatch, params: { category } } = this.props
    dispatch(unqueueCategoryItems(category))
  }

  getPageMeta() {
    const { category, path } = this.props

    const title = category
      ? `${category.get('title')} | Tuoreimmat uutiset | Ampparit.com`
      : 'Uutiset | Tuoreimmat uutiset | Ampparit.com'
    const metaDescription = category?.get('desc') ||
      'Ampparit.com tarjoaa Suomen kattavimman uutiskatsauksen. Klikkaa ja löydä kätevästi kaikki tuoreimmat uutiset!'

    return {
      title,
      h1Text: category?.get('id') === CATEGORY_ID_UUTISET
        ? category.get('h1')
        : `${category?.get('h1')} | Uutiset`,
      metaDescription,
      ogTitle: category?.get('ogTitle') || title,
      ogDescription: category?.get('ogDesc') || metaDescription,
      ogImage: category?.get('ogImage') || createUrl(shareImg),
      canonicalUrl: createUrl(path),
    }
  }

  componentWillUnmount() {
    this.props.dispatch(changeActiveList(LIST_TYPE_NONE, LIST_NAME_NONE))
  }

  renderFailedToLoad = () => (
    <TopNotification>
      <p><b>Uutisten lataaminen epäonnistui.</b></p>
    </TopNotification>
  )

  renderBlacklistedNotification = () => (
    <TopNotification>
      <p><b>Olet piilottanut tämän kategorian.</b></p>
    </TopNotification>
  )

  renderListOptions = () => {
    const { category, dispatch, listWhitelisted, listBlacklisted } = this.props
    return (
      <ListOptions
        tag={ category }
        isCategory={ true }
        dispatch={ dispatch }
        isWhitelisted={ listWhitelisted }
        isBlacklisted={ listBlacklisted }
      />
    )
  }

  renderEmpty = () => <CategoryItemsEmpty category={ this.props.category } />

  renderNewsListWidgets = () => <NewsListWidgets category={ this.props.category } />

  render() {
    const { itemsForCategory, category, listBlacklisted, trendingFailedToLoad, trending, path } = this.props
    const { title, h1Text, metaDescription, ogTitle, ogDescription, ogImage, canonicalUrl } = this.getPageMeta()

    return (
      <Fragment>
        <Helmet>
          <title>{ title }</title>
          <meta name='description' content={ metaDescription } />
          <meta property='og:title' content={ ogTitle } />
          <meta property='og:image' content={ ogImage } />
          <meta property='og:description' content={ ogDescription } />
          <meta property='og:url' content={ canonicalUrl } />
          <link rel='canonical' href={ canonicalUrl } />
        </Helmet>
        <NewsListView
          h1Text={ h1Text }
          list={ itemsForCategory }
          loading={ false }
          listBlacklisted={ listBlacklisted }
          trendingFailedToLoad={ trendingFailedToLoad }
          trending={ trending }
          renderBlacklistedNotification={ this.renderBlacklistedNotification }
          renderListOptions={ this.renderListOptions }
          renderEmpty={ this.renderEmpty }
          renderNewsListWidgets={ this.renderNewsListWidgets }
          handleFetchNext={ this.fetchNext }
          category={ category }
          tag={ null }
          path={ path }
          refreshList={ this.refreshList }
          unqueueItems={ this.unqueueItems }
          renderFailedToLoad={ this.renderFailedToLoad }
          onListDidUpdate={ this.handleListDidUpdate }
          setActiveList={ this.setActiveList }
        />
      </Fragment>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const { trending } = state

  const categorySlug = ownProps.params.category
  const itemsForCategory = state.items.categories.get(categorySlug)

  const category = selectCategoryBySlug(state, categorySlug)
  const isBlacklisted = category != null && selectUserBlacklistedCategoryIds(state).contains(category.id)
  const isWhitelisted = category != null && selectUserWhitelistedCategoryIds(state).contains(category.id)

  return {
    itemsForCategory,
    category,
    listBlacklisted: isBlacklisted,
    listWhitelisted: isWhitelisted,
    loggedIn: selectIsUserLoggedIn(state),
    trendingFailedToLoad: trending.get('failedToLoad'),
    trending: trending,
  }
}

export default connect(mapStateToProps)(CategoryView)
